import Vue from "vue";
import { mapGetters, mapState } from "vuex";

import buttonScComponent from "@/components/child/form/buttonSc.vue";
import comboComponent from "@/components/child/form/combo.vue";
import { Component, Prop, Watch } from "@/decorators";
import SituacoesManipulacaoOrdem from "@/models/enum/situacoesManipulacaoOrdem";
import TiposItemManipulacao from "@/models/enum/tiposItemManipulacao";
import KitEmbalagemModel from "@/models/kitEmbalagemModel";
import ManipulacaoOrdemItemModel from "@/models/manipulacaoOrdemItemModel";
import UsuarioModel from "@/models/usuarioModel";
import kitEmbalagemService from "@/services/kitEmbalagemService";
import {
    ItemTableType,
    ManipulacaoContextNewActions,
    ManipulacaoContextNewMutations,
    ManipulacaoContextNewState,
} from "@/store/manipulacaoContextNew/types";
import { AppState, Getters } from "@/store/store";
import Delay from "@/utils/common/delay";

import ManipulacaoItemComponent from "../item";
import manipulacaoItemComponent from "../item.vue";

@Component({
    components: {
        comboComponent,
        manipulacaoItemComponent,
        buttonScComponent,
    },
    computed: {
        ...mapState<AppState>({
            usuarioLogado: (state: AppState) => state.session.usuarioLogado,
        }),
        ...mapGetters(["GET_MANIPULACAO_CONTEXT_NEW"] as Getters),
    },
})
export default class ManipulacaoItensEmbalagensComponent extends Vue {
    // State computed props
    usuarioLogado!: UsuarioModel;
    GET_MANIPULACAO_CONTEXT_NEW!: (uuid: string) => ManipulacaoContextNewState;

    @Prop({ type: String, required: true }) uuid!: string;
    @Prop({ type: Number, default: 0 }) setorId!: number;

    private theader: string[] = [];
    private theaderColSpan: number[] = [];
    private itensReference: ManipulacaoItemComponent[] = [];
    private manipulacaoContext = new ManipulacaoContextNewState();
    private unsubscribe: (() => void) | null = null;
    private unsubscribeActions: (() => void) | null = null;

    public clear() {
        this.onRefazerHeader();

        for (const item of this.itensReference) {
            item.clear();
        }
    }

    public async validateAll() {
        for (const item of this.itensReference) {
            const isValid = await item.validateAll();
            if (!isValid) {
                return false;
            }
        }
        return true;
    }

    @Watch("manipulacaoContext.manipulacao.situacao")
    private onSituacaoChange() {
        this.loadKitEmbalagem();
    }

    public async loadKitEmbalagem() {
        if (this.manipulacaoContext?.isFranquiaFilial) {
            let kitEmbalagens = await kitEmbalagemService
                .getByEstoqueId(this.manipulacaoContext.manipulacao.estoqueId ?? 0)
                .withLoading()
                .resolveWithJSON<KitEmbalagemModel[]>();

            if (this.manipulacaoContext.manipulacao.situacao != SituacoesManipulacaoOrdem.Concluido) {
                kitEmbalagens = kitEmbalagens.filter(p => p.ativo);
            }

            const kitEmbalagensOptions = [...kitEmbalagens.map(p => ({ value: p.id, text: p.descricao }))];

            await this.dispatch(ManipulacaoContextNewActions.SET_KIT_EMBALAGEM_OPTIONS_NEW, kitEmbalagensOptions);
        } else {
            let kitEmbalagensOptions = await kitEmbalagemService.comboCached(this.usuarioLogado.franquiaId ?? 0);

            if (this.manipulacaoContext.manipulacao.situacao != SituacoesManipulacaoOrdem.Concluido) {
                kitEmbalagensOptions = kitEmbalagensOptions.filter(p => p.ativo);
            }

            await this.dispatch(ManipulacaoContextNewActions.SET_KIT_EMBALAGEM_OPTIONS_NEW, kitEmbalagensOptions);
        }
    }

    public addEmptyRow() {
        this.addNewRow(null);
    }

    private onAdicionarFormulaBase() {
        const itemModel = new ManipulacaoOrdemItemModel();
        itemModel.tipoItem = TiposItemManipulacao.FormulaPadrao;

        this.addNewRow(itemModel);
    }

    private async addNewRow(item: ManipulacaoOrdemItemModel | null) {
        await this.dispatch(ManipulacaoContextNewActions.ADD_ITEM_MANIPULACAO_NEW, {
            item,
            type: ItemTableType.EMBALAGEM,
        });

        await this.loadItensReference();

        this.onRefazerHeader();
    }

    private async removeItem(index: number) {
        this.dispatch(ManipulacaoContextNewActions.REMOVE_ITEM_MANIPULACAO_NEW, {
            type: ItemTableType.EMBALAGEM,
            index,
        });

        this.onRefazerHeader();
    }

    private onRefazerHeader() {
        this.theader = [
            this.$t("__.ts.prod") as string,
            this.$t("__.ts.quantidade") as string,
            this.$t("__.ts.qtdTotal") as string,
            this.$t("__.ts.total") as string,
        ];
        this.theaderColSpan = [6, 2, 2, 2];
    }

    private onAlterarVolumeCapsula(volume: number, descricao: string) {
        this.$emit("onAlterarVolumeCapsula", volume, descricao);
    }

    private onCorCapsulaChange(model: ManipulacaoOrdemItemModel) {
        this.$emit("onCorCapsulaChange", model);
    }

    private onConsolidado(consolidado: boolean) {
        this.$emit("onConsolidado", consolidado);
    }

    @Watch("formaFarmaceuticaId")
    private async onChangedFormaFarmaceutica() {
        this.loadManipulacaoContext();
    }

    private async loadItensReference() {
        this.itensReference = [];

        if (this.manipulacaoContext.embalagens.length > 0) {
            while (!this.$refs.manipulacaoItemComponent) await Delay(100);
            const refs = this.$refs.manipulacaoItemComponent as ManipulacaoItemComponent[];
            for (let index = 0; index < refs.length; index++) {
                this.itensReference[index] = refs[index];
            }
        }
    }

    private onChangeKitEmbalagem(id: number) {
        if (id) {
            this.dispatch(ManipulacaoContextNewActions.UPDATE_KIT_EMBALAGEM_ID_NEW, id);
        }
    }

    public forceUpdate() {
        this.$forceUpdate();

        this.loadItensReference();

        for (const item of this.itensReference) {
            item.forceUpdate();
        }
    }

    private async dispatch(action: ManipulacaoContextNewActions, data: unknown) {
        await this.$store.dispatch(action, { uuid: this.uuid, data });

        this.loadManipulacaoContext();
    }

    public refreshManipulacaoContext() {
        this.loadManipulacaoContext();

        for (const item of this.itensReference) {
            item.loadManipulacaoContext();
        }
    }

    private loadManipulacaoContext() {
        this.manipulacaoContext = this.GET_MANIPULACAO_CONTEXT_NEW(this.uuid);
    }

    public unsubscribeAll() {
        this.unsubscribe?.();
        this.unsubscribeActions?.();

        for (const item of this.itensReference) {
            item.unsubscribeAll();
        }
    }

    private async mounted() {
        this.unsubscribe = this.$store.subscribe(mutation => {
            switch (mutation.type) {
                case ManipulacaoContextNewMutations.ADD_ITEM_MANIPULACAO_NEW:
                case ManipulacaoContextNewMutations.REMOVE_ALL_ITENS_BY_TYPE_NEW:
                case ManipulacaoContextNewMutations.REMOVE_ITEM_MANIPULACAO_NEW:
                case ManipulacaoContextNewMutations.UPDATE_MANIPULACAO_NEW:
                case ManipulacaoContextNewMutations.REMOVE_EMBALAGENS_BY_KIT_ID_NEW:
                case ManipulacaoContextNewMutations.REMOVE_ATIVOS_ASSOCIADOS_NEW:
                case ManipulacaoContextNewMutations.CONSOLIDAR_NEW:
                    this.$forceUpdate();
                    this.loadManipulacaoContext();
                    break;
                case ManipulacaoContextNewMutations.CALCULAR_QSP_NEW:
                    this.$forceUpdate();
                    this.loadManipulacaoContext();
                    break;
            }
        });

        this.unsubscribeAll = this.$store.subscribeAction({
            after: action => {
                switch (action.type) {
                    case ManipulacaoContextNewActions.ADD_ITEM_MANIPULACAO_NEW:
                    case ManipulacaoContextNewActions.CALCULAR_QSP_NEW:
                    case ManipulacaoContextNewActions.REMOVE_ATIVOS_ASSOCIADOS_NEW:
                        this.$forceUpdate();
                        break;
                }
            },
        });

        this.onRefazerHeader();
        this.loadManipulacaoContext();
    }
}
